const CONSISTENCY_TOKEN_STORAGE_KEY = 'graphqlConsistencyTokens';
const MAX_STORED_CONSISTENCY_TOKENS = 5;

export const CONSISTENCY_TOKEN_HEADER = 'Resq-Consistency-Token';
export const CONSISTENCY_SATISFIED_HEADER = 'Resq-Consistency-Satisfied';

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

const getSessionStorage = (): StorageLike | null => {
    try {
        return window.sessionStorage ?? null;
    } catch {
        return null;
    }
};

const readStoredTokens = (storage: StorageLike | null): string[] => {
    if (!storage) {
        return [];
    }

    const rawValue = storage.getItem(CONSISTENCY_TOKEN_STORAGE_KEY);
    if (!rawValue) {
        return [];
    }

    try {
        const parsedValue = JSON.parse(rawValue);
        if (!Array.isArray(parsedValue)) {
            return [];
        }
        return parsedValue.filter(
            (token): token is string => typeof token === 'string' && token.length > 0,
        );
    } catch {
        return [];
    }
};

const writeStoredTokens = (tokens: string[], storage: StorageLike | null): void => {
    if (!storage) {
        return;
    }

    if (tokens.length === 0) {
        storage.removeItem(CONSISTENCY_TOKEN_STORAGE_KEY);
        return;
    }

    storage.setItem(CONSISTENCY_TOKEN_STORAGE_KEY, JSON.stringify(tokens));
};

export const getConsistencyTokenHeaderValue = (storage = getSessionStorage()): string | null => {
    const tokens = readStoredTokens(storage);
    return tokens.length > 0 ? tokens.join(', ') : null;
};

export const storeConsistencyToken = (
    token: string | null | undefined,
    storage = getSessionStorage(),
): void => {
    const trimmedToken = token?.trim();
    if (!trimmedToken) {
        return;
    }

    const existingTokens = readStoredTokens(storage).filter((value) => value !== trimmedToken);
    writeStoredTokens(
        [...existingTokens, trimmedToken].slice(-MAX_STORED_CONSISTENCY_TOKENS),
        storage,
    );
};

export const clearConsistencyTokens = (storage = getSessionStorage()): void => {
    if (!storage) {
        return;
    }

    storage.removeItem(CONSISTENCY_TOKEN_STORAGE_KEY);
};

export const syncConsistencyTokensFromHeaders = (
    headers: Headers,
    storage = getSessionStorage(),
): void => {
    const consistencyToken = headers.get(CONSISTENCY_TOKEN_HEADER);
    if (consistencyToken) {
        storeConsistencyToken(consistencyToken, storage);
        return;
    }

    if (headers.get(CONSISTENCY_SATISFIED_HEADER) === '1') {
        clearConsistencyTokens(storage);
    }
};
